import {
  AuditOutlined,
  CalendarOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  ExceptionOutlined,
  LogoutOutlined,
  RiseOutlined,
  SettingOutlined,
  ShareAltOutlined,
  SolutionOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Button, Layout, Menu, Space, Tag, Typography } from 'antd'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { ROLE_LABELS, type AdminRole } from '../api/types'
import { useAuth } from '../store/auth'
import './AdminLayout.css'

/**
 * 菜单按角色过滤。
 *
 * ⚠️ 这只是**体验层**的收敛，不是权限本身 —— 真正的权限在后端 AdminGuard。
 * 前端藏菜单只是为了不让人点进一个必然 403 的页面，
 * 绝不能反过来假设「菜单看不见 = 接口调不到」。
 */
const NAV: { key: string; label: string; icon: React.ReactNode; roles: AdminRole[] }[] = [
  {
    key: '/users',
    label: '用户',
    icon: <UserOutlined />,
    roles: ['SUPER', 'CONTENT', 'ANALYST'],
  },
  {
    key: '/attempts',
    label: '答题记录',
    icon: <SolutionOutlined />,
    roles: ['SUPER', 'CONTENT', 'ANALYST'],
  },
  {
    key: '/shares',
    label: '分享记录',
    icon: <ShareAltOutlined />,
    roles: ['SUPER', 'CONTENT', 'ANALYST'],
  },
  {
    key: '/dashboard',
    label: '数据看板',
    icon: <DashboardOutlined />,
    roles: ['SUPER', 'CONTENT', 'ANALYST'],
  },
  {
    key: '/growth',
    label: '增长健康',
    icon: <RiseOutlined />,
    roles: ['SUPER', 'CONTENT', 'ANALYST'],
  },
  {
    key: '/content',
    label: '内容运营',
    icon: <CalendarOutlined />,
    roles: ['SUPER', 'CONTENT'],
  },
  {
    key: '/tickets',
    label: '纠错工单',
    icon: <ExceptionOutlined />,
    roles: ['SUPER', 'CONTENT', 'REVIEWER'],
  },
  {
    key: '/config',
    label: '规则配置',
    icon: <SettingOutlined />,
    roles: ['SUPER'],
  },
  {
    key: '/questions',
    label: '题库',
    icon: <DatabaseOutlined />,
    roles: ['SUPER', 'CONTENT', 'ANALYST', 'REVIEWER'],
  },
  { key: '/review', label: '待复核', icon: <AuditOutlined />, roles: ['SUPER', 'REVIEWER'] },
]

export default function AdminLayout() {
  const { me, loading, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  if (loading) return null
  if (!me) return <Navigate to="/login" replace />

  const items = NAV.filter((n) => n.roles.includes(me.role)).map((n) => ({
    key: n.key,
    label: n.label,
    icon: n.icon,
  }))

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Header className="admin-header">
        <Typography.Text strong className="admin-brand">
          带娃练习生 · 运营后台
        </Typography.Text>
        <Menu
          className="admin-menu"
          theme="dark"
          mode="horizontal"
          disabledOverflow
          selectedKeys={[items.find((i) => location.pathname.startsWith(i.key))?.key ?? '']}
          items={items}
          onClick={(e) => navigate(e.key)}
        />
        <Space className="admin-account">
          <Tag className="admin-role">{ROLE_LABELS[me.role]}</Tag>
          <Typography.Text className="admin-display-name">{me.displayName}</Typography.Text>
          <Button size="small" icon={<LogoutOutlined />} onClick={logout} title="退出登录">
            退出
          </Button>
        </Space>
      </Layout.Header>

      <Layout.Content className="admin-content">
        <Outlet />
      </Layout.Content>
    </Layout>
  )
}
